'use client';

import { AtomicContext } from '@contexts/coveo/AtomicContext';
import { buildPager } from '@coveo/atomic-react';
import { MicrocopyItem } from '@lib/types/cms/MicrocopySet';
import { Maybe } from '@lib/types/Maybe';
import c from 'classnames';
import { CaretLeft, CaretRight } from 'phosphor-icons';
import { useContext, useEffect, useState } from 'react';

const commonButtonClasses =
  'size-10 mx-1 rounded-lg cursor-pointer border text-grey-600 border-grey-300 text-base hover:bg-grey-100 flex items-center justify-center';
const arrowButtonStyles = c(
  commonButtonClasses,
  'disabled:bg-grey-300 disabled:cursor-not-allowed disabled:text-grey-400'
);
const pageButtonStyles = c(
  commonButtonClasses,
  'font-bold disabled:bg-blue disabled:border-blue disabled:text-white disabled:cursor-not-allowed'
);

type Props = {
  paginationMicrocopySet: Maybe<MicrocopyItem[]>;
  context: AtomicContext;
};

export const HeadlessPager = ({ paginationMicrocopySet, context }: Props) => {
  const { searchEngine } = useContext(context);
  const [pagerController] = useState(searchEngine && buildPager(searchEngine));
  const [pagerState, setPagerState] = useState(pagerController?.state);

  const scrollToTop = () => {
    window.scrollTo({ top: 0 });
    const firstProductCardElement = document.querySelector(
      'atomic-layout-section[section="results"] article:first-child a'
    ) as HTMLElement;
    firstProductCardElement?.focus();
  };

  const previousPage = () => {
    pagerController?.previousPage();
    scrollToTop();
  };

  const selectPage = (page: number) => {
    pagerController?.selectPage(page);
    scrollToTop();
  };

  const nextPage = () => {
    pagerController?.nextPage();
    scrollToTop();
  };

  const {
    paginationMicrocopy,
    previousMicrocopy,
    pageMicrocopy,
    nextMicrocopy,
  } = getMicrocopyValues(paginationMicrocopySet);

  useEffect(
    () =>
      pagerController?.subscribe(() => setPagerState(pagerController?.state)),
    [pagerController]
  );

  return (
    <nav data-test="pager" aria-label={paginationMicrocopy}>
      <ul className="flex">
        <li>
          <button
            aria-label={previousMicrocopy}
            className={arrowButtonStyles}
            role="link"
            disabled={!pagerState?.hasPreviousPage}
            aria-disabled={!pagerState?.hasPreviousPage}
            onClick={previousPage}
          >
            <CaretLeft size={20} role="presentation" />
          </button>
        </li>
        {pagerState?.currentPages.map((page) => (
          <li key={page}>
            <button
              aria-label={`${pageMicrocopy} ${page}`}
              className={pageButtonStyles}
              role="link"
              disabled={pagerController?.isCurrentPage(page)}
              aria-current={pagerController?.isCurrentPage(page) && 'page'}
              onClick={() => selectPage(page)}
            >
              {page}
            </button>
          </li>
        ))}
        <li>
          <button
            aria-label={nextMicrocopy}
            className={arrowButtonStyles}
            role="link"
            disabled={!pagerState?.hasNextPage}
            aria-disabled={!pagerState?.hasNextPage}
            onClick={nextPage}
          >
            <CaretRight size={20} role="presentation" />
          </button>
        </li>
      </ul>
    </nav>
  );
};

const getMicrocopyValues = (microcopySet: Maybe<MicrocopyItem[]>) => {
  const paginationMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.pagination-aria-label'
  )?.value;
  const previousMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.previous-aria-label'
  )?.value;
  const pageMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.page-aria-label'
  )?.value;
  const nextMicrocopy = microcopySet?.find(
    (microcopyFragment) =>
      microcopyFragment.key === 'pagination.next-aria-label'
  )?.value;

  return {
    paginationMicrocopy: paginationMicrocopy ?? 'Pagination',
    previousMicrocopy: previousMicrocopy ?? 'Previous',
    pageMicrocopy: pageMicrocopy ?? 'Page',
    nextMicrocopy: nextMicrocopy ?? 'Next',
  };
};

export default HeadlessPager;
